
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Reveal } from './Reveal';
import { ChevronLeft, ChevronRight, Activity, Scale, Factory } from 'lucide-react';

export const CaseStudies: React.FC = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);

  const cases = [
    {
      title: t('case_studies.items.radiology.title'),
      icon: <Activity size={32} />,
      stat: "-30%",
      statLabel: t('case_studies.items.radiology.stat_label'),
      quote: t('case_studies.items.radiology.quote'),
      author: "Dr. med. Thomas W.",
      role: t('case_studies.items.radiology.role')
    },
    {
      title: t('case_studies.items.law.title'),
      icon: <Scale size={32} />,
      stat: "45 Min",
      statLabel: t('case_studies.items.law.stat_label'),
      quote: t('case_studies.items.law.quote'),
      author: "Mag. Julia B.",
      role: t('case_studies.items.law.role')
    },
    {
      title: t('case_studies.items.industry.title'),
      icon: <Factory size={32} />,
      stat: "100%",
      statLabel: t('case_studies.items.industry.stat_label'),
      quote: t('case_studies.items.industry.quote'),
      author: "Ing. Markus L.",
      role: t('case_studies.items.industry.role')
    }
  ];

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % cases.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev === 0 ? cases.length - 1 : prev - 1));
  };

  // Auto-advance
  useEffect(() => {
    const timer = setInterval(nextSlide, 8000);
    return () => clearInterval(timer);
  }, []);

  // Logic to determine position class for infinite loop effect
  const getSlidePositionClass = (index: number) => {
    if (index === currentIndex) return 'translate-x-0 opacity-100 z-20 scale-100';

    // Calculate the "previous" index wrapping around (e.g. if current is 0, prev is 2)
    const prevIndex = (currentIndex - 1 + cases.length) % cases.length;
    if (index === prevIndex) return '-translate-x-full opacity-0 z-10 scale-95'; // Exit Left

    // Calculate the "next" index wrapping around (e.g. if current is 2, next is 0)
    const nextIndex = (currentIndex + 1) % cases.length;
    if (index === nextIndex) return 'translate-x-full opacity-0 z-10 scale-95'; // Wait Right

    // Default for others
    return 'translate-x-full opacity-0 z-0 hidden';
  };

  return (
    <section id="cases" className="py-20 md:py-40 bg-rabbit-navy text-white border-t border-gray-800 relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-[600px] h-[600px] bg-blue-900/10 blur-[120px] pointer-events-none rounded-full"></div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">

        {/* Header */}
        <Reveal>
          <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-800 pb-8">
            <h2 className="text-5xl md:text-7xl font-bold text-white tracking-tighter leading-none">
              {t('case_studies.title_line1')}<br />{t('case_studies.title_line2')}
            </h2>
            <div className="flex gap-4 mt-8 md:mt-0">
              <button
                onClick={prevSlide}
                className="p-4 border border-gray-700 hover:bg-white hover:text-black hover:border-white transition-all duration-300 rounded-full group"
                aria-label="Previous Case Study"
              >
                <ChevronLeft size={24} className="text-gray-400 group-hover:text-black transition-colors" />
              </button>
              <button
                onClick={nextSlide}
                className="p-4 border border-gray-700 hover:bg-white hover:text-black hover:border-white transition-all duration-300 rounded-full group"
                aria-label="Next Case Study"
              >
                <ChevronRight size={24} className="text-gray-400 group-hover:text-black transition-colors" />
              </button>
            </div>
          </div>
        </Reveal>

        {/* Carousel Content */}
        <div className="relative min-h-[600px] md:min-h-[450px]">
          {cases.map((item, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-all duration-700 ease-[cubic-bezier(0.23,1,0.32,1)] transform ${getSlidePositionClass(index)}`}
            >
              <div className="grid md:grid-cols-12 gap-12 items-center h-full">

                {/* Left: Content */}
                <div className="md:col-span-7 flex flex-col h-full justify-center">
                  <div className="flex items-center gap-4 mb-10">
                    <div className="p-3 bg-white/10 rounded-lg text-white border border-white/10">
                      {item.icon}
                    </div>
                    <span className="text-sm font-bold uppercase tracking-[0.2em] text-blue-200">{item.title}</span>
                  </div>

                  {/* Quote with improved spacing/leading */}
                  <blockquote className="text-2xl md:text-4xl font-light leading-[1.5] mb-12 text-white font-sans tracking-wide">
                    "{item.quote}"
                  </blockquote>

                  <div className="pl-4 border-l-2 border-clever-teal">
                    <div className="font-bold text-lg text-white">{item.author}</div>
                    <div className="text-gray-400 text-sm">{item.role}</div>
                  </div>
                </div>

                {/* Right: Big Metric (Navy Design) */}
                <div className="md:col-span-5 flex justify-start md:justify-end items-center">
                  <div className="bg-[#0F172A] p-12 w-full text-center border border-gray-800 relative overflow-hidden group hover:border-gray-700 transition-colors">
                    <div className="absolute top-0 left-0 w-1 h-full bg-clever-teal"></div>
                    <div className="text-6xl md:text-7xl font-bold text-clever-teal mb-4 tracking-tight group-hover:scale-110 transition-transform duration-500">{item.stat}</div>
                    <div className="text-gray-400 text-xs font-bold uppercase tracking-widest">{item.statLabel}</div>
                  </div>
                </div>

              </div>
            </div>
          ))}
        </div>

        {/* Progress Bar */}
        <div className="flex gap-0 mt-12 md:mt-0 h-1 w-full bg-gray-800 max-w-xs">
          {cases.map((_, idx) => (
            <div
              key={idx}
              className="flex-1 h-full bg-gray-800 relative overflow-hidden border-r border-rabbit-navy last:border-0"
            >
              <div
                className={`absolute inset-0 bg-clever-teal transition-transform duration-[8000ms] linear ${idx === currentIndex ? 'translate-x-0' : '-translate-x-full'}`}
                style={{ transitionProperty: idx === currentIndex ? 'transform' : 'none' }}
              ></div>
              <div className={`absolute inset-0 bg-white/20 transition-opacity duration-300 ${idx === currentIndex ? 'opacity-100' : 'opacity-0'}`}></div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};
